import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { Property } from '../models/property';
import { Client } from '../models/client';
import { Visit } from '../models/visit';
import { Contract, Payment } from '../models/contract';
import { ExpenseImmobilier } from '../models/expense-immobilier';
import { Maintenance } from '../models/maintenance';
import { User } from '../models/user';
import { padStart } from '../utils/string.utils';

@Injectable({
  providedIn: 'root'
})
export class ImmobilierService {

  private baseUrl = 'https://formation.hybraine.com/mboufta/backend/api';

  constructor(private http: HttpClient) { }

  private getHeaders(): HttpHeaders {
    const token = sessionStorage.getItem('samacaabiyoonwii');
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    if (token) {
      headers = headers.set('samacaabiyoonwii', token);
    }
    return headers;
  }

  // ============ AUTHENTIFICATION ============

  login(credentials: { login: string, pwd: string }): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/login.php`, credentials, { headers: this.getHeaders() });
  }

  logout(token: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/logout.php`, { token }, { headers: this.getHeaders() });
  }

  // ============ GESTION DES BIENS IMMOBILIERS ============

  getProperties(): Observable<Property[]> {
    return this.http.get<any[]>(`${this.baseUrl}/properties.php`, { headers: this.getHeaders() }).pipe(
      map(data => data.map(item => this.mapPropertyFromApi(item)))
    );
  }

  getPropertyById(id: number): Observable<Property> {
    return this.http.get<any>(`${this.baseUrl}/properties.php?id=${id}`, { headers: this.getHeaders() }).pipe(
      map(item => this.mapPropertyFromApi(item))
    );
  }

  createProperty(property: Property): Observable<any> {
    const apiData = this.mapPropertyToApi(property);
    return this.http.post(`${this.baseUrl}/properties.php`, apiData, { headers: this.getHeaders() });
  }

  updateProperty(id: number, property: Property): Observable<any> {
    const apiData = this.mapPropertyToApi({ ...property, id });
    return this.http.put(`${this.baseUrl}/properties.php`, apiData, { headers: this.getHeaders() });
  }

  deleteProperty(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/properties.php?id=${id}`, { headers: this.getHeaders() });
  }

  searchProperties(filters: any): Observable<Property[]> {
    // Client-side filtering for now as backend doesn't support advanced search yet
    return this.getProperties().pipe(
      map(properties => {
        let filtered = [...properties];

        if (filters.type) {
          filtered = filtered.filter(p => p.type === filters.type);
        }
        if (filters.status) {
          filtered = filtered.filter(p => p.status === filters.status);
        }
        if (filters.transactionType) {
          filtered = filtered.filter(p => p.transactionType === filters.transactionType);
        }
        if (filters.city) {
          filtered = filtered.filter(p => p.city && p.city.toLowerCase().includes(filters.city.toLowerCase()));
        }
        if (filters.minPrice) {
          filtered = filtered.filter(p => p.price >= filters.minPrice);
        }
        if (filters.maxPrice) {
          filtered = filtered.filter(p => p.price <= filters.maxPrice);
        }
        if (filters.minSurface) {
          filtered = filtered.filter(p => p.surface >= filters.minSurface);
        }
        if (filters.maxSurface) {
          filtered = filtered.filter(p => p.surface <= filters.maxSurface);
        }

        return filtered;
      })
    );
  }

  private mapPropertyFromApi(data: any): Property {
    return {
      ...data,
      transactionType: data.transaction_type || data.transactionType,
      totalFloors: data.total_floors || data.totalFloors,
      hasElevator: Boolean(Number(data.has_elevator)),
      hasParking: Boolean(Number(data.has_parking)),
      hasGarden: Boolean(Number(data.has_garden)),
      hasBalcony: Boolean(Number(data.has_balcony)),
      hasTerrace: Boolean(Number(data.has_terrace)),
      hasPool: Boolean(Number(data.has_pool)),
      hasAirConditioning: Boolean(Number(data.has_air_conditioning)),
      hasHeating: Boolean(Number(data.has_heating)),
      yearBuilt: data.year_built || data.yearBuilt,
      energyClass: data.energy_class || data.energyClass,
      gesClass: data.ges_class || data.gesClass,
      virtualTourUrl: data.virtual_tour_url || data.virtualTourUrl,
      floorPlan: data.floor_plan || data.floorPlan,
      availabilityDate: data.availability_date ? new Date(data.availability_date) : undefined,
      isFurnished: Boolean(Number(data.is_furnished)),
      createdAt: new Date(data.created_at || data.createdAt),
      updatedAt: new Date(data.updated_at || data.updatedAt),
      lastVisitDate: data.last_visit_date ? new Date(data.last_visit_date) : undefined,
      visitCount: Number(data.visit_count || 0),
      // Handle JSON fields if they come as strings
      images: typeof data.images === 'string' ? JSON.parse(data.images || '[]') : (data.images || []),
      videos: typeof data.videos === 'string' ? JSON.parse(data.videos || '[]') : (data.videos || [])
    };
  }

  private mapPropertyToApi(property: Property): any {
    return {
      id: property.id,
      reference: property.reference,
      type: property.type,
      status: property.status,
      transaction_type: property.transactionType,
      title: property.title,
      description: property.description,
      address: property.address,
      city: property.city,
      postal_code: property.postalCode,
      country: property.country,
      surface: property.surface,
      rooms: property.rooms,
      bedrooms: property.bedrooms,
      bathrooms: property.bathrooms,
      price: property.price,
      charges: property.charges,
      deposit: property.deposit,
      fees: property.fees,

      floor: property.floor,
      total_floors: property.totalFloors,
      year_built: property.yearBuilt,
      energy_class: property.energyClass,
      ges_class: property.gesClass,

      has_elevator: property.hasElevator ? 1 : 0,
      has_parking: property.hasParking ? 1 : 0,
      has_garden: property.hasGarden ? 1 : 0,
      has_balcony: property.hasBalcony ? 1 : 0,
      has_terrace: property.hasTerrace ? 1 : 0,
      has_pool: property.hasPool ? 1 : 0,
      has_air_conditioning: property.hasAirConditioning ? 1 : 0,
      has_heating: property.hasHeating ? 1 : 0,

      virtual_tour_url: property.virtualTourUrl,
      floor_plan: property.floorPlan,
      images: property.images,
      videos: property.videos,

      availability_date: property.availabilityDate instanceof Date ? property.availabilityDate.toISOString().split('T')[0] : property.availabilityDate,
      is_furnished: property.isFurnished ? 1 : 0
    };
  }

  // ============ GESTION DES CLIENTS ============

  getClients(): Observable<Client[]> {
    return this.http.get<any[]>(`${this.baseUrl}/clients.php`, { headers: this.getHeaders() }).pipe(
      map(data => data.map(item => this.mapClientFromApi(item)))
    );
  }

  getUsers(): Observable<User[]> {
    return this.http.get<any[]>(`${this.baseUrl}/users.php`, { headers: this.getHeaders() }).pipe(
      map(data => data.map(item => this.mapUserFromApi(item)))
    );
  }

  createUser(user: User): Observable<any> {
    const apiData = this.mapUserToApi(user);
    return this.http.post(`${this.baseUrl}/users.php`, apiData, { headers: this.getHeaders() });
  }

  updateUser(id: number, user: User): Observable<any> {
    const apiData = this.mapUserToApi({ ...user, id });
    return this.http.put(`${this.baseUrl}/users.php`, apiData, { headers: this.getHeaders() });
  }

  deleteUser(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/users.php?id=${id}`, { headers: this.getHeaders() });
  }

  private mapUserFromApi(data: any): User {
    return {
      ...data,
      firstName: data.first_name || data.firstName,
      lastName: data.last_name || data.lastName
    };
  }

  private mapUserToApi(user: User): any {
    return {
      id: user.id,
      login: user.login,
      password: user.password,
      first_name: user.firstName,
      last_name: user.lastName,
      profile: user.profile,
      status: user.status
    };
  }

  getClientById(id: number): Observable<Client> {
    return this.http.get<any>(`${this.baseUrl}/clients.php?id=${id}`, { headers: this.getHeaders() }).pipe(
      map(item => this.mapClientFromApi(item))
    );
  }

  createClient(client: Client): Observable<any> {
    const apiData = this.mapClientToApi(client);
    return this.http.post(`${this.baseUrl}/clients.php`, apiData, { headers: this.getHeaders() });
  }

  updateClient(id: number, client: Client): Observable<any> {
    const apiData = this.mapClientToApi({ ...client, id });
    return this.http.put(`${this.baseUrl}/clients.php`, apiData, { headers: this.getHeaders() });
  }

  deleteClient(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/clients.php?id=${id}`, { headers: this.getHeaders() });
  }

  private mapClientFromApi(data: any): Client {
    return {
      ...data,
      personType: data.person_type || data.personType,
      firstName: data.first_name || data.firstName,
      lastName: data.last_name || data.lastName,
      mobilePhone: data.mobile_phone || data.mobilePhone,
      postalCode: data.postal_code || data.postalCode,
      preferredPropertyTypes: typeof data.preferred_property_types === 'string' ? JSON.parse(data.preferred_property_types || '[]') : (data.preferredPropertyTypes || []),
      minPrice: Number(data.min_price || data.minPrice),
      maxPrice: Number(data.max_price || data.maxPrice),
      minSurface: Number(data.min_surface || data.minSurface),
      maxSurface: Number(data.max_surface || data.maxSurface),
      preferredLocations: typeof data.preferred_locations === 'string' ? JSON.parse(data.preferred_locations || '[]') : (data.preferredLocations || []),
      financingType: data.financing_type || data.financingType,
      hasFinancingPreApproval: Boolean(Number(data.has_financing_pre_approval)),
      interestLevel: data.interest_level || data.interestLevel,
      conversionDate: data.conversion_date ? new Date(data.conversion_date) : undefined,
      visitCount: Number(data.visit_count || 0),
      propertyViews: Number(data.property_views || 0),
      favoriteProperties: typeof data.favorite_properties === 'string' ? JSON.parse(data.favorite_properties || '[]') : (data.favoriteProperties || []),
      lastContactDate: data.last_contact_date ? new Date(data.last_contact_date) : undefined,
      createdAt: new Date(data.created_at || data.createdAt),
      updatedAt: new Date(data.updated_at || data.updatedAt),
      createdBy: data.created_by || data.createdBy,
      updatedBy: data.updated_by || data.updatedBy,

      // KYC Personne
      birthDate: data.birth_date ? new Date(data.birth_date) : undefined,
      birthPlace: data.birth_place,
      nationality: data.nationality,
      maritalStatus: data.marital_status,
      matrimonialRegime: data.matrimonial_regime,
      marriageDate: data.marriage_date ? new Date(data.marriage_date) : undefined,
      marriagePlace: data.marriage_place,
      currentFunction: data.current_function,
      previousProfession: data.previous_profession,
      fatherProfession: data.father_profession,
      motherProfession: data.mother_profession,

      // KYC Conjoint
      spouseFirstName: data.spouse_first_name,
      spouseLastName: data.spouse_last_name,
      spouseBirthDate: data.spouse_birth_date ? new Date(data.spouse_birth_date) : undefined,
      spouseBirthPlace: data.spouse_birth_place,
      spouseNationality: data.spouse_nationality,
      spouseProfession: data.spouse_profession,

      // KYC Paiement & Fonds
      preferredPaymentMethod: data.preferred_payment_method,
      preferredPaymentDate: data.preferred_payment_date,
      fundsOrigin: data.funds_origin
    };
  }

  private mapClientToApi(client: Client): any {
    return {
      id: client.id,
      type: client.type,
      person_type: client.personType,
      first_name: client.firstName,
      last_name: client.lastName,
      email: client.email,
      phone: client.phone,
      mobile_phone: client.mobilePhone,
      address: client.address,
      city: client.city,
      postal_code: client.postalCode,
      country: client.country,
      profession: client.profession,
      company: client.company,
      preferred_property_types: JSON.stringify(client.preferredPropertyTypes || []),
      min_price: client.minPrice,
      max_price: client.maxPrice,
      min_surface: client.minSurface,
      max_surface: client.maxSurface,
      preferred_locations: JSON.stringify(client.preferredLocations || []),
      budget: client.budget,
      financing_type: client.financingType,
      has_financing_pre_approval: client.hasFinancingPreApproval ? 1 : 0,
      status: client.status,
      source: client.source,
      notes: client.notes,
      interest_level: client.interestLevel,
      conversion_date: client.conversionDate instanceof Date ? client.conversionDate.toISOString().split('T')[0] : client.conversionDate,

      // KYC Personne
      birth_date: client.birthDate instanceof Date ? client.birthDate.toISOString().split('T')[0] : client.birthDate,
      birth_place: client.birthPlace,
      nationality: client.nationality,
      marital_status: client.maritalStatus,
      matrimonial_regime: client.matrimonialRegime,
      marriage_date: client.marriageDate instanceof Date ? client.marriageDate.toISOString().split('T')[0] : client.marriageDate,
      marriage_place: client.marriagePlace,
      current_function: client.currentFunction,
      previous_profession: client.previousProfession,
      father_profession: client.fatherProfession,
      mother_profession: client.motherProfession,

      // KYC Conjoint
      spouse_first_name: client.spouseFirstName,
      spouse_last_name: client.spouseLastName,
      spouse_birth_date: client.spouseBirthDate instanceof Date ? client.spouseBirthDate.toISOString().split('T')[0] : client.spouseBirthDate,
      spouse_birth_place: client.spouseBirthPlace,
      spouse_nationality: client.spouseNationality,
      spouse_profession: client.spouseProfession,

      // KYC Paiement
      preferred_payment_method: client.preferredPaymentMethod,
      preferred_payment_date: client.preferredPaymentDate,
      funds_origin: client.fundsOrigin
    };
  }

  private mapVisitToApi(visit: Visit): any {
    return {
      id: visit.id,
      property_id: visit.propertyId,
      client_id: visit.clientId,
      agent_id: visit.agentId,
      type: visit.type,
      visit_date: visit.visitDate instanceof Date ? visit.visitDate.toISOString().split('T')[0] : visit.visitDate,
      visit_time: visit.visitTime,
      meeting_point: visit.meetingPoint,
      contact_on_site: visit.contactOnSite,
      internal_instructions: visit.internalInstructions,
      client_feedback: visit.clientFeedback,
      agent_feedback: visit.agentFeedback,
      follow_up_date: visit.followUpDate instanceof Date ? visit.followUpDate.toISOString().split('T')[0] : visit.followUpDate,
      follow_up_notes: visit.followUpNotes,
      reminder_sent: visit.reminderSent ? 1 : 0,
      reminder_date: visit.reminderDate instanceof Date ? visit.reminderDate.toISOString().split('T')[0] : visit.reminderDate,
      notify_sms: visit.notifySms ? 1 : 0,
      notify_email: visit.notifyEmail ? 1 : 0,
      points_positifs: visit.pointsPositifs,
      decision_client: visit.decisionClient,
      notes: visit.notes,
      status: visit.status
    };
  }

  // ============ GESTION DES VISITES ============

  getVisits(): Observable<Visit[]> {
    return this.http.get<any[]>(`${this.baseUrl}/visits.php`, { headers: this.getHeaders() }).pipe(
      map(data => data.map(item => this.mapVisitFromApi(item)))
    );
  }

  getVisitsByProperty(propertyId: number): Observable<Visit[]> {
    // Client-side filtering
    return this.getVisits().pipe(
      map(visits => visits.filter(v => v.propertyId === propertyId))
    );
  }

  getVisitsByClient(clientId: number): Observable<Visit[]> {
    // Client-side filtering
    return this.getVisits().pipe(
      map(visits => visits.filter(v => v.clientId === clientId))
    );
  }

  createVisit(visit: Visit): Observable<any> {
    const apiData = this.mapVisitToApi(visit);
    return this.http.post(`${this.baseUrl}/visits.php`, apiData, { headers: this.getHeaders() });
  }

  updateVisit(id: number, visit: Visit): Observable<any> {
    const apiData = this.mapVisitToApi({ ...visit, id });
    return this.http.put(`${this.baseUrl}/visits.php`, apiData, { headers: this.getHeaders() });
  }

  deleteVisit(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/visits.php?id=${id}`, { headers: this.getHeaders() });
  }

  private mapVisitFromApi(data: any): Visit {
    return {
      ...data,
      propertyId: Number(data.property_id || data.propertyId),
      clientId: Number(data.client_id || data.clientId),
      agentId: Number(data.agent_id || data.agentId),
      propertyReference: data.property_reference || data.propertyReference,
      propertyName: data.property_name || data.propertyName,
      clientName: data.client_name || data.clientName,
      agentName: data.agent_name || data.agentName,
      visitDate: new Date(data.visit_date || data.visitDate),
      visitTime: data.visit_time || data.visitTime,
      meetingPoint: data.meeting_point || data.meetingPoint,
      contactOnSite: data.contact_on_site || data.contactOnSite,
      internalInstructions: data.internal_instructions || data.internalInstructions,
      clientFeedback: data.client_feedback || data.clientFeedback,
      agentFeedback: data.agent_feedback || data.agentFeedback,
      followUpDate: data.follow_up_date ? new Date(data.follow_up_date) : undefined,
      followUpNotes: data.follow_up_notes || data.followUpNotes,
      reminderSent: Boolean(Number(data.reminder_sent)),
      reminderDate: data.reminder_date ? new Date(data.reminder_date) : undefined,
      notifySms: Boolean(Number(data.notify_sms)),
      notifyEmail: Boolean(Number(data.notify_email)),
      pointsPositifs: data.points_positifs || data.pointsPositifs,
      decisionClient: data.decision_client || data.decisionClient,
      createdAt: new Date(data.created_at || data.createdAt),
      updatedAt: new Date(data.updated_at || data.updatedAt)
    };
  }

  // ============ GESTION DES CONTRATS ============

  getContracts(): Observable<Contract[]> {
    return this.http.get<any[]>(`${this.baseUrl}/contracts.php`, { headers: this.getHeaders() }).pipe(
      map(data => data.map(item => this.mapContractFromApi(item)))
    );
  }

  getContractById(id: number): Observable<Contract> {
    return this.http.get<any>(`${this.baseUrl}/contracts.php?id=${id}`, { headers: this.getHeaders() }).pipe(
      map(item => this.mapContractFromApi(item))
    );
  }

  createContract(contract: Contract, file?: File): Observable<any> {
    if (file) {
      const formData = this.toFormData(contract, file);
      return this.http.post(`${this.baseUrl}/contracts.php`, formData, { headers: this.getAuthHeadersOnly() });
    }
    // Note: On devrait aussi mapper en snake_case pour le JSON si le backend l'exige, mais je ne touche pas au flux JSON existant pour ne pas introduire de régression si ça marchait différemment.
    // Cependant, vu le code PHP, il est probable qu'il faille mapper. Je vais assumer pour l'instant que le JSON marche tel quel ou que le user gérera ça, 
    // ou je devrais mapper aussi le JSON. Dans le doute, je ne touche PAS au cas sans fichier pour l'instant.
    return this.http.post(`${this.baseUrl}/contracts.php`, contract, { headers: this.getHeaders() });
  }

  updateContract(id: number, contract: Contract, file?: File): Observable<any> {
    const formData = this.toFormData({ ...contract, id }, file);
    // Toujours utiliser POST pour profiter de la logique complète (FormData) du backend
    return this.http.post(`${this.baseUrl}/contracts.php`, formData, { headers: this.getAuthHeadersOnly() });
  }

  deleteContract(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/contracts.php?id=${id}`, { headers: this.getHeaders() });
  }

  downloadContractFile(filename: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/download_contract.php?file=${filename}`, {
      headers: this.getAuthHeadersOnly(),
      responseType: 'blob'
    });
  }

  private getAuthHeadersOnly(): HttpHeaders {
    const token = sessionStorage.getItem('samacaabiyoonwii');
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('samacaabiyoonwii', token);
    }
    return headers;
  }

  private toFormData(data: any, file?: File): FormData {
    const formData = new FormData();
    if (file) {
      formData.append('contractFile', file);
    }

    // Mapping manuel des champs pour correspondre aux attentes du backend (snake_case)
    if (data.id) formData.append('id', data.id);
    if (data.contractNumber) formData.append('contract_number', data.contractNumber);
    if (data.type) formData.append('type', data.type);
    if (data.propertyId) formData.append('property_id', data.propertyId.toString());
    if (data.ownerId) formData.append('owner_id', data.ownerId.toString());
    if (data.tenantBuyerId) formData.append('tenant_buyer_id', data.tenantBuyerId.toString());

    // Gestion des dates
    if (data.startDate) {
      const d = new Date(data.startDate);
      formData.append('start_date', d.toISOString().split('T')[0]);
    }
    if (data.endDate) {
      const d = new Date(data.endDate);
      formData.append('end_date', d.toISOString().split('T')[0]);
    }

    if (data.signatureDate) {
      console.log('Appending signature_date:', data.signatureDate);
      // Si c'est déjà une chaine ISO, on coupe simplement
      if (typeof data.signatureDate === 'string' && data.signatureDate.includes('T')) {
        formData.append('signature_date', data.signatureDate.split('T')[0]);
      } else {
        const d = new Date(data.signatureDate);
        formData.append('signature_date', d.toISOString().split('T')[0]);
      }
    }

    if (data.status) formData.append('status', data.status);

    // Financials
    if (data.monthlyRent) formData.append('monthly_rent', data.monthlyRent.toString());
    if (data.salePrice) formData.append('sale_price', data.salePrice.toString());
    if (data.deposit) formData.append('deposit', data.deposit.toString());
    if (data.charges) formData.append('charges', data.charges.toString());
    if (data.fees) formData.append('fees', data.fees.toString());

    // Payment
    if (data.paymentMethod) formData.append('payment_method', data.paymentMethod);
    if (data.paymentDay) formData.append('payment_day', data.paymentDay.toString());

    // Others
    formData.append('indexation', data.indexation ? '1' : '0');
    if (data.specialConditions) formData.append('special_conditions', data.specialConditions);

    return formData;
  }

  private mapContractFromApi(data: any): Contract {
    return {
      ...data,
      contractNumber: data.contract_number || data.contractNumber,
      propertyId: Number(data.property_id || data.propertyId),
      ownerId: Number(data.owner_id || data.ownerId),
      tenantBuyerId: Number(data.tenant_buyer_id || data.tenantBuyerId),
      startDate: new Date(data.start_date || data.startDate),
      endDate: data.end_date ? new Date(data.end_date) : undefined,
      monthlyRent: Number(data.monthly_rent || data.monthlyRent),
      salePrice: Number(data.sale_price || data.salePrice),
      deposit: Number(data.deposit || data.deposit),
      charges: Number(data.charges || data.charges),
      fees: Number(data.fees || data.fees),
      paymentMethod: data.payment_method || data.paymentMethod,
      paymentDay: Number(data.payment_day || data.paymentDay),
      indexation: Boolean(Number(data.indexation)),
      signatureDate: data.signature_date ? new Date(data.signature_date) : undefined,
      signedByOwner: Boolean(Number(data.signed_by_owner)),
      signedByTenant: Boolean(Number(data.signed_by_tenant)),
      contractDocumentUrl: data.contract_document_url || data.contractDocumentUrl,
      inventoryUrl: data.inventory_url || data.inventoryUrl,
      specialConditions: data.special_conditions || data.specialConditions,
      createdAt: new Date(data.created_at || data.createdAt),
      updatedAt: new Date(data.updated_at || data.updatedAt),

      // Mapped from JOINs or fallback
      ownerName: 'MBOUFTA',
      tenantBuyerName: data.tenant_buyer_name || data.tenantBuyerName,
      propertyReference: data.property_reference || data.propertyReference,
      propertyName: data.property_name || data.propertyName
    };
  }

  // ============ GESTION DES PAIEMENTS ============

  getPaymentsByContract(contractId: number): Observable<Payment[]> {
    return this.http.get<any[]>(`${this.baseUrl}/payments.php?contractId=${contractId}`, { headers: this.getHeaders() }).pipe(
      map(data => data.map(item => this.mapPaymentFromApi(item)))
    );
  }

  getAllPayments(): Observable<Payment[]> {
    return this.http.get<any[]>(`${this.baseUrl}/payments.php`, { headers: this.getHeaders() }).pipe(
      map(data => data.map(item => this.mapPaymentFromApi(item)))
    );
  }

  createPayment(payment: Payment): Observable<any> {
    const apiData = this.mapPaymentToApi(payment);
    return this.http.post(`${this.baseUrl}/payments.php`, apiData, { headers: this.getHeaders() });
  }

  updatePayment(id: number, payment: Payment): Observable<any> {
    const apiData = this.mapPaymentToApi({ ...payment, id });
    return this.http.put(`${this.baseUrl}/payments.php`, apiData, { headers: this.getHeaders() });
  }

  deletePayment(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/payments.php?id=${id}`, { headers: this.getHeaders() });
  }

  private mapPaymentFromApi(data: any): Payment {
    return {
      ...data,
      contractId: Number(data.contract_id || data.contractId),
      amount: Number(data.amount),
      paymentDate: new Date(data.payment_date || data.paymentDate),
      dueDate: data.due_date ? new Date(data.due_date) : undefined,
      type: data.type,
      status: data.status,
      paymentMethod: data.payment_method || data.paymentMethod,
      reference: data.reference,
      notes: data.notes,
      propertyName: data.propertyName || data.property_name,
      clientName: data.clientName || data.client_name
    };
  }

  private mapPaymentToApi(payment: Payment): any {
    return {
      id: payment.id,
      contractId: payment.contractId,
      amount: payment.amount,
      paymentDate: payment.paymentDate instanceof Date ? payment.paymentDate.toISOString().split('T')[0] : payment.paymentDate,
      dueDate: payment.dueDate instanceof Date ? payment.dueDate.toISOString().split('T')[0] : payment.dueDate,
      type: payment.type,
      status: payment.status,
      paymentMethod: payment.paymentMethod,
      reference: payment.reference,
      notes: payment.notes
    };
  }

  // ============ GESTION DES DEPENSES ============

  getExpenses(): Observable<ExpenseImmobilier[]> {
    return this.http.get<any[]>(`${this.baseUrl}/expenses.php`, { headers: this.getHeaders() }).pipe(
      map(data => data.map(item => this.mapExpenseFromApi(item)))
    );
  }

  createExpense(expense: ExpenseImmobilier): Observable<any> {
    return this.http.post(`${this.baseUrl}/expenses.php`, expense, { headers: this.getHeaders() });
  }

  updateExpense(id: number, expense: ExpenseImmobilier): Observable<any> {
    return this.http.put(`${this.baseUrl}/expenses.php`, { ...expense, id }, { headers: this.getHeaders() });
  }

  deleteExpense(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/expenses.php?id=${id}`, { headers: this.getHeaders() });
  }

  private mapExpenseFromApi(data: any): ExpenseImmobilier {
    return {
      ...data,
      propertyId: Number(data.property_id || data.propertyId),
      maintenanceId: Number(data.maintenance_id || data.maintenanceId),
      taxAmount: Number(data.tax_amount || data.taxAmount),
      totalAmount: Number(data.total_amount || data.totalAmount),
      expenseDate: new Date(data.expense_date || data.expenseDate),
      periodStart: data.period_start ? new Date(data.period_start) : undefined,
      periodEnd: data.period_end ? new Date(data.period_end) : undefined,
      paymentMethod: data.payment_method || data.paymentMethod,
      paymentDate: data.payment_date ? new Date(data.payment_date) : undefined,
      paymentStatus: data.payment_status || data.paymentStatus,
      providerId: Number(data.provider_id || data.providerId),
      providerName: data.provider_name || data.providerName,
      providerPhone: data.provider_phone || data.providerPhone,
      providerEmail: data.provider_email || data.providerEmail,
      invoiceUrl: data.invoice_url || data.invoiceUrl,
      receiptUrl: data.receipt_url || data.receiptUrl,
      isRecoverable: Boolean(Number(data.is_recoverable)),
      recoverableAmount: Number(data.recoverable_amount || data.recoverableAmount),
      createdAt: new Date(data.created_at || data.createdAt),
      updatedAt: new Date(data.updated_at || data.updatedAt),
      propertyTitle: data.property_name || data.propertyTitle,
      propertyReference: data.property_reference || data.propertyReference
    };
  }

  // ============ GESTION DES MAINTENANCES ============

  getMaintenances(): Observable<Maintenance[]> {
    return this.http.get<any[]>(`${this.baseUrl}/maintenances.php`, { headers: this.getHeaders() }).pipe(
      map(data => data.map(item => this.mapMaintenanceFromApi(item)))
    );
  }

  createMaintenance(maintenance: Maintenance): Observable<any> {
    const apiData = this.mapMaintenanceToApi(maintenance);
    return this.http.post(`${this.baseUrl}/maintenances.php`, apiData, { headers: this.getHeaders() });
  }

  updateMaintenance(id: number, maintenance: Maintenance): Observable<any> {
    const apiData = this.mapMaintenanceToApi({ ...maintenance, id });
    return this.http.put(`${this.baseUrl}/maintenances.php`, apiData, { headers: this.getHeaders() });
  }

  deleteMaintenance(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/maintenances.php?id=${id}`, { headers: this.getHeaders() });
  }

  private mapMaintenanceFromApi(data: any): Maintenance {
    return {
      ...data,
      propertyId: Number(data.property_id || data.propertyId),
      requestDate: new Date(data.request_date || data.requestDate),
      scheduledDate: data.scheduled_date ? new Date(data.scheduled_date) : undefined,
      completionDate: data.completion_date ? new Date(data.completion_date) : undefined,
      dueDate: data.due_date ? new Date(data.due_date) : undefined,
      providerId: Number(data.provider_id || data.providerId),
      providerName: data.provider_name || data.providerName,
      providerPhone: data.provider_phone || data.providerPhone,
      providerEmail: data.provider_email || data.providerEmail,
      estimatedCost: Number(data.estimated_cost || data.estimatedCost),
      actualCost: Number(data.actual_cost || data.actualCost),
      laborCost: Number(data.labor_cost || data.laborCost),
      materialCost: Number(data.material_cost || data.materialCost),
      invoiceUrl: data.invoice_url || data.invoiceUrl,
      internalNotes: data.internal_notes || data.internalNotes,
      createdAt: new Date(data.created_at || data.createdAt),
      updatedAt: new Date(data.updated_at || data.updatedAt),
      photos: typeof data.photos === 'string' ? JSON.parse(data.photos || '[]') : (data.photos || []),
      propertyTitle: data.property_name || data.propertyTitle,
      propertyReference: data.property_reference || data.propertyReference
    };
  }

  private mapMaintenanceToApi(maintenance: Maintenance): any {
    return {
      id: maintenance.id,
      reference: maintenance.reference,
      property_id: maintenance.propertyId,
      type: maintenance.type,
      category: maintenance.category,
      title: maintenance.title,
      description: maintenance.description,
      request_date: maintenance.requestDate instanceof Date ? maintenance.requestDate.toISOString().split('T')[0] : maintenance.requestDate,
      scheduled_date: maintenance.scheduledDate instanceof Date ? maintenance.scheduledDate.toISOString().split('T')[0] : maintenance.scheduledDate,
      completion_date: maintenance.completionDate instanceof Date ? maintenance.completionDate.toISOString().split('T')[0] : maintenance.completionDate,
      due_date: maintenance.dueDate instanceof Date ? maintenance.dueDate.toISOString().split('T')[0] : maintenance.dueDate,
      status: maintenance.status,
      priority: maintenance.priority,
      provider_id: maintenance.providerId,
      provider_name: maintenance.providerName,
      provider_phone: maintenance.providerPhone,
      provider_email: maintenance.providerEmail,
      estimated_cost: maintenance.estimatedCost,
      actual_cost: maintenance.actualCost,
      labor_cost: maintenance.laborCost,
      material_cost: maintenance.materialCost,
      invoice_url: maintenance.invoiceUrl,
      internal_notes: maintenance.internalNotes,
      notes: maintenance.notes
    };
  }

  // ============ STATISTIQUES ============

  getStatistics(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/statistics.php`, { headers: this.getHeaders() });
  }

  // Audit
  getAuditLogs(): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/audit.php`, { headers: this.getHeaders() });
  }

  // ============ GESTION DES INFORMATIONS BANCAIRES ENTREPRISE ============

  getCompanyBankInfo(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/company_settings.php`, { headers: this.getHeaders() });
  }

  saveCompanyBankInfo(bankInfo: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/company_settings.php`, bankInfo, { headers: this.getHeaders() });
  }
}
